import type { ArtistSummary, ConcentrationBand, VintageEra } from '@dashboard/contract';
import { Donut, type Slice } from './Donut';

const BAND_LABEL: Record<ConcentrationBand, string> = {
  top1: 'Top song',
  '2-5': 'Songs 2–5',
  '6-10': 'Songs 6–10',
  '11-20': 'Songs 11–20',
  '21+': 'Songs 21+',
};
const BAND_COLOR: Record<ConcentrationBand, string> = {
  top1: 'var(--e5)',
  '2-5': 'var(--e4)',
  '6-10': 'var(--e3)',
  '11-20': 'var(--e2)',
  '21+': 'var(--sother)',
};
const ERA_LABEL: Record<VintageEra, string> = {
  'pre-2015': 'Pre-2015',
  '2015-17': '2015–17',
  '2018-20': '2018–20',
  '2021-23': '2021–23',
  '2024+': '2024+',
  unknown: 'Unknown year',
};
const ERA_COLOR: Record<VintageEra, string> = {
  'pre-2015': 'var(--e1)',
  '2015-17': 'var(--e2)',
  '2018-20': 'var(--e3)',
  '2021-23': 'var(--e4)',
  '2024+': 'var(--e5)',
  unknown: 'var(--sother)',
};
const RAMP = ['var(--e5)', 'var(--e4)', 'var(--e3)', 'var(--e2)', 'var(--e1)'];

export function MarketsDonut({ summary }: { summary: ArtistSummary }) {
  const m = summary.markets;
  const sub = `Top ${m.top.length || 5} markets · LTM · artist-level`;
  if (m.status === 'not_ingested') {
    return (
      <article className="donut">
        <h3>Where the streams come from</h3>
        <div className="sub">{sub}</div>
        <p className="dempty">
          No per-market streams are ingested for {summary.artistName}: only the worldwide aggregate
          is held. Ask for the territories to be ingested through Ghost.
        </p>
      </article>
    );
  }
  const slices: Slice[] = m.top.map((t, i) => ({
    label: `${t.name}${t.premiumOnly ? ' (premium only)' : ''}${t.complete ? '' : ' (partial)'}`,
    value: t.streams,
    color: RAMP[i] ?? 'var(--e1)',
  }));
  if (m.restOfWorld !== null)
    slices.push({ label: 'Rest of world', value: m.restOfWorld, color: 'var(--sother)' });
  return (
    <Donut title="Where the streams come from" sub={sub} slices={slices}>
      {m.top.some((t) => t.premiumOnly) ? (
        <p className="dempty">
          Outside US/CA Luminate delivers premium streams only, so those slices are indicative.
        </p>
      ) : null}
    </Donut>
  );
}

export function ConcentrationDonut({ summary }: { summary: ArtistSummary }) {
  return (
    <Donut
      title="How concentrated the catalogue is"
      sub={`Share of worldwide LTM streams by rank · ${summary.trackCount} tracks`}
      slices={summary.concentration.map((c) => ({
        label: BAND_LABEL[c.band],
        value: c.streams,
        color: BAND_COLOR[c.band],
      }))}
    />
  );
}

export function VintageDonut({ summary }: { summary: ArtistSummary }) {
  return (
    <Donut
      title="What vintage is earning"
      sub="Share of worldwide LTM streams by release year"
      slices={summary.vintage.map((v) => ({
        label: ERA_LABEL[v.era],
        value: v.streams,
        color: ERA_COLOR[v.era],
      }))}
    />
  );
}
